import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';

/**
 * Um renderizador de markdown mínimo, só para o que o assistente realmente
 * escreve: parágrafos, listas com marcador ou número, e `**negrito**`/`*itálico*`
 * dentro da linha.
 *
 * Não é uma biblioteca de markdown — é de propósito. A fala do agente vem em um
 * subconjunto pequeno e previsível; puxar uma dependência (com `cssInterop` no
 * nativo) por isso seria peso morto. Qualquer coisa que não seja marcação
 * conhecida cai como texto normal, então nunca "some" caractere.
 */
export function MarkdownText({ text, color }: { text: string; color: string }) {
  const blocks = parseBlocks(text);

  return (
    <View style={{ gap: 6 }}>
      {blocks.map((block, index) => {
        if (block.type === 'list') {
          return (
            <View key={index} style={{ gap: 3 }}>
              {block.items.map((item, itemIndex) => (
                <View
                  key={itemIndex}
                  style={{ flexDirection: 'row', gap: 6 }}
                >
                  <ThemedText style={{ color }}>{item.marker}</ThemedText>
                  <ThemedText style={{ color, flex: 1 }}>
                    <Inline text={item.text} />
                  </ThemedText>
                </View>
              ))}
            </View>
          );
        }
        return (
          <ThemedText key={index} style={{ color }}>
            <Inline text={block.text} />
          </ThemedText>
        );
      })}
    </View>
  );
}

type Block =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: { marker: string; text: string }[] };

/** Quebra o texto em parágrafos e blocos de lista, olhando linha a linha. */
function parseBlocks(text: string): Block[] {
  const lines = text.split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push({ type: 'paragraph', text: paragraph.join('\n') });
    paragraph = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    // `- item`, `* item` ou `1. item` — captura o marcador e o resto.
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*(\d+)\.\s+(.*)$/);

    if (bullet || numbered) {
      flushParagraph();
      const last = blocks[blocks.length - 1];
      const item = bullet
        ? { marker: '•', text: bullet[1] }
        : { marker: `${numbered![1]}.`, text: numbered![2] };
      if (last && last.type === 'list') {
        last.items.push(item);
      } else {
        blocks.push({ type: 'list', items: [item] });
      }
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    paragraph.push(line);
  }

  flushParagraph();
  return blocks;
}

/** Aplica `**negrito**` e `*itálico*` dentro de uma linha. */
function Inline({ text }: { text: string }) {
  const parts = splitInline(text);
  return (
    <>
      {parts.map((part, index) => (
        // Sem `color` aqui de propósito: no React Native o Text aninhado herda a
        // cor do Text de fora (a da bolha), então cada span segue a cor certa.
        <ThemedText
          key={index}
          style={{
            fontWeight: part.bold ? '700' : undefined,
            fontStyle: part.italic ? 'italic' : undefined,
          }}
        >
          {part.text}
        </ThemedText>
      ))}
    </>
  );
}

type Span = { text: string; bold?: boolean; italic?: boolean };

/**
 * Fatia a linha nos marcadores `**` (negrito) e `*` (itálico). O `**` é testado
 * antes do `*` para não confundir um com o outro.
 */
function splitInline(text: string): Span[] {
  const spans: Span[] = [];
  const regex = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > cursor) {
      spans.push({ text: text.slice(cursor, match.index) });
    }
    if (match[1] !== undefined) {
      spans.push({ text: match[1], bold: true });
    } else {
      spans.push({ text: match[2], italic: true });
    }
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    spans.push({ text: text.slice(cursor) });
  }
  if (spans.length === 0) {
    spans.push({ text });
  }
  return spans;
}
